import clsx from 'clsx';
import { useReducedMotion } from 'motion/react';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import { SidePanelExpandableContext } from './context';
import styles from './PageWithSidePanel.module.css';

export interface PageWithSidePanelProps {
    /** Main content always displayed. */
    children: React.ReactNode;
    /** Side-content to render, null if it should be closed. */
    sidePanel: React.ReactNode | null;
}

/**
 * Component to render a main body with a side-panel on the right.
 */
export function PageWithSidePanel(props: PageWithSidePanelProps): React.ReactElement {
    const { children, sidePanel } = props;

    const [expanded, setExpanded] = React.useState(false);
    const shouldReduceMotion = useReducedMotion();
    const nodeRef = React.useRef<HTMLDivElement>(null);

    const expandableContext = React.useMemo(() => ({ expanded, setExpanded }), [expanded]);

    const transitionDuration = shouldReduceMotion ? 0 : 180;

    // Keep track of the last displayed children to continue rendering them during the unmount phase.
    const lastDisplayedChildrenRef = React.useRef<React.ReactNode>(children);
    if (sidePanel) {
        lastDisplayedChildrenRef.current = sidePanel;
    }

    const openedSidePanel = !!sidePanel;

    return (
        <SidePanelExpandableContext.Provider value={expandableContext}>
            <div
                className={clsx(
                    'flex min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-side transition-padding',
                    styles.page,
                    openedSidePanel ? styles.pageWithOpenedSidePanel : null,
                    expanded ? styles.pageWithExpandedSidePanel : null,
                    shouldReduceMotion ? styles.shouldReduceMotion : null
                )}
            >
                <div
                    className="app-framing-styles relative flex flex-1 flex-col"
                    // Allow the parent AppLayout to detect whether a child screen is rendering with sidepanels, to skip framing styles. See app.css for more.
                    data-side-panel-frame
                >
                    {children}
                </div>
                <CSSTransition
                    in={openedSidePanel}
                    timeout={transitionDuration}
                    nodeRef={nodeRef}
                    appear
                    classNames={{
                        appear: styles.appear,
                        appearActive: styles.appearActive,
                        appearDone: styles.appearDone,
                        enter: styles.enter,
                        enterActive: styles.enterActive,
                        enterDone: styles.enterDone,
                        exit: styles.exit,
                        exitActive: styles.exitActive,
                        exitDone: styles.exitDone,
                    }}
                    unmountOnExit
                    onExited={() => {
                        // After the side panel is closed, we reset the expanded state,
                        // so that next time it opened, the side panel has the default width.
                        setExpanded(false);
                    }}
                >
                    <div
                        ref={nodeRef}
                        className={clsx(
                            // The class names here should not be dynamic based on state as CSSTransition will control them using the ref.
                            'absolute top-1.5 right-1.5 bottom-1.5 z-10 flex flex-col overflow-hidden rounded-xl border-0.5 border-control bg-base shadow-subtle',
                            styles.sidePanel
                        )}
                    >
                        {sidePanel ?? lastDisplayedChildrenRef.current}
                    </div>
                </CSSTransition>
            </div>
        </SidePanelExpandableContext.Provider>
    );
}
